/**
 * Base Widget Preview Utilities
 *
 * Generic utilities for widget iframe previews.
 * Widget-specific composers should import and use these.
 */

import { sendToWidget, PARENT_TO_WIDGET } from '@/js/widget/widget-messaging.js';

/**
 * Build widget iframe source URL
 * @param {Object} config - Configuration
 * @param {string} config.widget_name - Widget name (e.g., 'guest-check-in', 'contact-form'). Corresponds to test html file name
 * @param {string} config.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} config.is_development - Whether in development mode
 * @param {number} config.dev_port - Development server port (default: 5175)
 * @returns {string} Widget iframe source URL
 *
 * @example
 * buildWidgetIframeSrc({
 *   widget_name: 'guest-check-in',
 *   preview_mode: 'mobile',
 *   is_development: true
 * })
 */
export function buildWidgetIframeSrc(config) {
  const {
    widget_name,
    preview_mode = 'tablet-landscape',
    is_development = false,
    dev_port = 5175,
  } = config;

  const is_staging = window.location.hostname.includes('staging');

  let widget_file_name = widget_name;
  if (is_staging) {
    widget_file_name = `staging-${widget_name}`;
  }

  const base_url = is_development
    ? `http://localhost:${dev_port}/`
    : `https://files.redchirp.com/v1/prod/widget/${widget_file_name}.html`;

  const mobile_param = preview_mode === 'mobile' ? '?mobile=true' : '';

  return `${base_url}${mobile_param}`;
}

/**
 * Send settings to widget iframe and ensure it's shown
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {Object} settings - Settings to send to widget
 */
export function sendSettingsToWidget(iframe, settings) {
  sendToWidget(iframe, PARENT_TO_WIDGET.SET_SETTINGS, settings);
  sendToWidget(iframe, PARENT_TO_WIDGET.SHOW);
}

/**
 * Send page change message to widget iframe
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {string} page - Page to navigate to
 */
export function sendPageChangeToWidget(iframe, page) {
  sendToWidget(iframe, PARENT_TO_WIDGET.NAVIGATE_TO_PAGE, page);
}

/**
 * Send auto-fill test data message to widget
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {boolean} recycle_data - Whether to recycle existing data if present. Useful when applying edits, where the test data should remain the same across renders
 */
export function sendAutoFillTestData(iframe, recycle_data = false) {
  sendToWidget(iframe, PARENT_TO_WIDGET.AUTO_FILL_TEST_DATA, recycle_data);
}

/**
 * Base settings structure for widget previews
 * @param {Object} config - Configuration
 * @param {string} config.form_id_key - Key name for form ID (e.g., 'guest_check_in_form_id')
 * @param {string} config.form_id - Form ID (real or preview ID)
 * @param {Object} config.form - Form data object
 * @param {boolean} config.is_test_submission - Whether this is a test submission
 * @param {boolean} config.is_template_preview - Whether this is a template preview
 * @param {string} config.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {string} config.page - Initial page to display
* @returns {Object} Base settings structure
 */
export function createBaseWidgetSettings(config) {
  const {
    form_id_key = 'form_id',
    form_id,
    form,
    is_test_submission = true,
    is_template_preview = false,
    preview_mode = 'tablet-landscape',
    page,
  } = config;

  const settings = {
    is_test_submission,
    preview_mode,
    is_template_preview,
    mobile_breakpoint: preview_mode === 'mobile' ? 0 : 600,
    form,
    page,
  };

  // Set form ID using widget-specific key
  settings[form_id_key] = form_id || `preview-${Date.now()}`;

  return settings;
}


